import { useEffect, useState } from 'react';
import { useLocation,useNavigate } from 'react-router-dom';

import GalleryIcon from '@/assets/all-icons/gallery-icon';
import ShareIcon from '@/assets/all-icons/share-icon';
import config from '@/config';
import useNotifications from "@/hooks/useNotifications";
import useStateHandler from "@/hooks/useStateHandler";
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import { getPublicGalleryQrVisits } from '@/services/galleryQrVisits.service';
/* import DirectMessageIcon from '@/shared-components/icons/components/channel/DirectMessageIcon'; */
// removed useGoBack
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import ProfileNotificationsIcon from '@/shared-components/icons/components/user-profile/ProfileNotificationsIcon';

import GalleryVisitsModal from './components/GalleryVisitsModal';
import NotificationModal from './components/NotificationModal';
import ProfileOptions from './components/ProfileOptions';
import FollowNotificationControl from './follow-notifications/FollowNotificationControl';

import './index.css';

function Notifications() {
  const [openModal, setOpenModal] = useState(false);
  const { cacheHandler } = useStateHandler();

  const location = useLocation();
  const navigate = useNavigate();
  const { hasNotifications, 
    markedNotifications, 
    setMarkedNotifications, 
    loadMoreData, 
    loading, 
    queryData, 
    hasBeenRead, 
    setHasBeenRead,
    fetch } = useNotifications();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('notifications') === 'show') {
      setOpenModal(true);
    } else {
      setOpenModal(false);
    }
  }, [location.search]);

  useEffect(() => {
    if(openModal)
    {
      const areNotificationsToUpdate = queryData?.data && queryData?.data?.data?.some(({ viewed, _id }) => !viewed && !markedNotifications.includes(_id));
      if(areNotificationsToUpdate) 
      {
        const notificationIds = queryData?.data?.data?.filter(({ viewed, _id }) => !viewed && !markedNotifications.includes(_id)).map(({ _id }) => _id);
        fetchWrapper.post(`${config.apis.api.url}/notification/mark-as-viewed`, {
          injectToken: true,
          body: { notificationIds }
        }).then(() => {
          setMarkedNotifications(prevIds => [...prevIds, ...notificationIds]);  
        });
      }
    }
  }, [openModal, queryData]);

  const handleOpenDialog = () => {
    // The inbox is cached while the profile remains mounted, so refresh it whenever it opens.
    fetch();

    const params = new URLSearchParams(location.search);
    params.set('notifications', 'show');
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleCloseDialog = () => {
    const params = new URLSearchParams(location.search);
    params.delete('notifications');
    navigate(`${location.pathname}?${params.toString()}`);
    setHasBeenRead(true);
    cacheHandler.storeInCache(`user_actions.notifications.read`, true, { type: "@cache/no-expiration" });
  };

  const handleOpenPreferences = () => {
    setHasBeenRead(true);
    cacheHandler.storeInCache(`user_actions.notifications.read`, true, { type: "@cache/no-expiration" });
    navigate('/profile/settings/notifications');
  };

  return (
    <>
      <div className="svg-notification-container">
        <ProfileNotificationsIcon className={`profile-top-buttons__notifications-icon hover-icon-animation__icon`} onClick={handleOpenDialog} />
        {hasNotifications && (
          <div className='new-notifications'></div>
        )}
      </div>
      <NotificationModal open={openModal} close={handleCloseDialog} loading={loading} 
        queryData={queryData} loadMoreData={loadMoreData} showNewNotifications={!hasBeenRead} fetch={fetch}
        onOpenPreferences={handleOpenPreferences} />
    </>);
}

export default function ProfileTopButtons({ fetchData, internal }) {
  const [open, setOpen] = useState(false);
  const [galleryVisitsOpen, setGalleryVisitsOpen] = useState(false);
  const [externalGalleryVisits, setExternalGalleryVisits] = useState(null);
  const t = useTranslations('common.profile.top_buttons');
  const hasNativeShare = Boolean(navigator?.share);
  const profileType = String(fetchData?.user_type || fetchData?.__t || '').trim().toLowerCase();
  const supportsGalleryVisits = profileType === 'artist' || profileType === 'collector';
  const username = fetchData?.user_username;
  const hasExternalGalleryVisits =
    externalGalleryVisits?.username === username
    && externalGalleryVisits.data.length > 0;
  const canShowGalleryVisits = supportsGalleryVisits && (internal || hasExternalGalleryVisits);
  const canManageFollowNotifications = !internal && fetchData?.im_following === true;

  useEffect(() => {
    if (internal || !supportsGalleryVisits || !username) {
      setExternalGalleryVisits(null);
      return undefined;
    }

    let active = true;
    setExternalGalleryVisits(null);

    getPublicGalleryQrVisits(username)
      .then((result) => {
        if (!active) return;
        setExternalGalleryVisits({
          username,
          data: Array.isArray(result?.data) ? result.data : [],
          next_cursor: result?.next_cursor || null,
        });
      })
      .catch(() => {
        if (active) setExternalGalleryVisits({ username, data: [], next_cursor: null });
      });

    return () => {
      active = false;
    };
  }, [internal, supportsGalleryVisits, username]);

  // NEW: read history & origin
  const navigate  = useNavigate();
  const location  = useLocation();
  const goBackHandler = () => {
    const { from, scrollY } = location.state || {};
    if (from != null && scrollY != null) {
      // restore scroll to exactly where we clicked the tag
      navigate(from, { state: { scrollY }, replace: true });
    } else if (window.history.length > 1) {
      // fallback to browser history
      navigate(-1);
    } else {
      // direct link → home
      navigate("/", { replace: true });
    }
  };

  const handleShare = async () => {
    if (!hasNativeShare) {
      return;
    }

    try {
      await navigator.share({
        title: t('share.title', { username: fetchData.user_username }),
        text: t('share.text', { username: fetchData.user_username }),
        url: `https://suarte.art/user/${fetchData.user_username}`
      });
    } catch (error) {
      // Intentional no-op: user cancelled the native share dialog.
    }
  };

  const galleryVisitsButton = (
    <button
      type="button"
      className={`profile-top-buttons__gallery-visits-button${internal ? ' profile-top-buttons__gallery-visits-button--internal' : ` profile-top-buttons__gallery-visits-button--external${hasNativeShare ? ' profile-top-buttons__gallery-visits-button--with-share' : ''}${canManageFollowNotifications ? ' profile-top-buttons__gallery-visits-button--with-follow-notifications' : ''}`}`}
      onClick={() => setGalleryVisitsOpen(true)}
      aria-label={t('visited_galleries.open')}
    >
      <GalleryIcon className="profile-top-buttons__gallery-visits-icon" />
    </button>
  );

  if(!internal) {
    return (
      <>
        <BackArrowIcon className={"profile-top-buttons__go-back"} onClick={goBackHandler}/>

        {canShowGalleryVisits && galleryVisitsButton}

        {canManageFollowNotifications && <FollowNotificationControl hasShare={hasNativeShare} profile={fetchData} />}

        {hasNativeShare && (
          <ShareIcon 
            className="profile-top-buttons__share-button" 
            onClick={handleShare}
          />
        )}

        {canShowGalleryVisits && (
          <GalleryVisitsModal
            key={username}
            open={galleryVisitsOpen}
            onClose={() => setGalleryVisitsOpen(false)}
            username={username}
            initialVisits={externalGalleryVisits}
          />
        )}
      </>
    );
  } else {
    return (
      <>
        {canShowGalleryVisits && galleryVisitsButton}

        <div className="profile-top-buttons__group">
          {/* <div
            className="profile-top-buttons__direct-message-icon"
            onClick={() => navigate(`/channels`, { state: { fromPath: location.pathname + location.search } })}
            style={{ cursor: 'pointer' }}
            title="Channels"
          >
            <DirectMessageIcon />
          </div> */}
          <Notifications />
          <ThreeDotsHorizontal className={`profile-top-buttons__profile-options-icon${open ? " active" : ""}`} onClick={() => setOpen(true)} />
        </div>
    
        <ProfileOptions 
          open={open} 
          setOpen={setOpen} 
          fetchData={fetchData}
        />
        {canShowGalleryVisits && (
          <GalleryVisitsModal
            key={username}
            open={galleryVisitsOpen}
            onClose={() => setGalleryVisitsOpen(false)}
            username={username}
          />
        )}
      </>
    );
  }
}
